import type { UserRole } from '../contexts/AuthContext';
import type { ModuleName, PermissionAction } from '../types/roles';

export type FeaturePermission = 'canCreateSupply' | 'canEditSupply' | 'canDeleteSupply' | 'canCreateVehicle' | 'canEditVehicle' | 'canDeleteVehicle' | 'canCreatePersonnel' | 'canEditPersonnel' | 'canDeletePersonnel' | 'canViewCostAnalysis' | 'canExportReports' | 'canManageUsers' | 'canViewSystemLogs' | 'canConfigureIntegrations' | 'canViewOwnTasks' | 'canUpdateTaskStatus' | 'canUploadDocuments' | 'canViewBidMarketplace' | 'canSubmitBid' | 'canViewOwnBids' | 'canWithdrawBid' | 'canManageWarehouse' | 'canScanDocuments';

type PermissionRule = {
  module: ModuleName;
  action: PermissionAction;
};

export const ROUTE_PERMISSIONS: Record<string, PermissionRule> = {
  '/': { module: 'dashboard', action: 'read' },
  '/dashboard': { module: 'dashboard', action: 'read' },
  '/live-tracking': { module: 'live-tracking', action: 'read' },
  '/supply': { module: 'supply-management', action: 'read' },
  '/tracking': { module: 'shipment-tracking', action: 'read' },
  '/tracking-list': { module: 'shipment-monitoring', action: 'read' },
  '/shipment-timeline': { module: 'shipment-monitoring', action: 'read' },
  '/fleet-status-list': { module: 'shipment-monitoring', action: 'read' },
  '/spot-status-list': { module: 'shipment-monitoring', action: 'read' },
  '/automations': { module: 'automations', action: 'read' },
  '/warehouse-transfer': { module: 'warehouse-transfer', action: 'read' },
  '/offer-portal': { module: 'offer-portal', action: 'read' },
  '/offer-requests': { module: 'offer-requests', action: 'read' },
  '/collaboration-management': { module: 'collaboration-management', action: 'read' },
  '/customer-management': { module: 'customer-management', action: 'read' },
  '/vehicle-management': { module: 'vehicle-management', action: 'read' },
  '/vehicle-management?ownerType=external': { module: 'corporate-external-vehicles', action: 'read' },
  '/vehicle-documents': { module: 'vehicle-documents', action: 'read' },
  '/weighbridge-points': { module: 'weighbridge-points', action: 'read' },
  '/vehicle-inspection-forms': { module: 'vehicle-inspection-forms', action: 'read' },
  '/vehicle-assignment-forms': { module: 'vehicle-assignment-forms', action: 'read' },
  '/vehicle-assignments': { module: 'vehicle-assignments', action: 'read' },
  '/trailer-management': { module: 'trailer-management', action: 'read' },
  '/driver-incident-reports': { module: 'driver-incident-reports', action: 'read' },
  '/traffic-fines': { module: 'traffic-fines', action: 'read' },
  '/seyir-driver-comparison': { module: 'seyir-driver-comparison', action: 'read' },
  '/drivers': { module: 'drivers', action: 'read' },
  '/driver-violations': { module: 'drivers', action: 'read' },
  '/personnel-files': { module: 'personnel-files', action: 'read' },
  '/gate-entry-exit': { module: 'gate-entry-exit', action: 'read' },
  '/driver-applications': { module: 'driver-applications', action: 'read' },
  '/approved-drivers': { module: 'approved-drivers', action: 'read' },
  '/corporate-drivers': { module: 'approved-drivers', action: 'read' },
  '/corporate-tracking-list': { module: 'corporate-carriers', action: 'read' },
  '/driver-leave-requests': { module: 'driver-leave-requests', action: 'read' },
  '/driver-payrolls': { module: 'driver-payrolls', action: 'read' },
  '/shift-management': { module: 'shift-management', action: 'read' },
  '/shift-assignments': { module: 'shift-assignments', action: 'read' },
  '/capacity': { module: 'capacity-planning', action: 'read' },
  '/documents': { module: 'documents', action: 'read' },
  '/shipment-monitoring': { module: 'shipment-monitoring', action: 'read' },
  '/decision-matrix': { module: 'shipment-tracking', action: 'read' },
  '/warehouse-ramp-assignment': { module: 'warehouse-ramp-assignment', action: 'read' },
  '/arrival-tracking': { module: 'shipment-monitoring', action: 'read' },
  '/point-visit-logs': { module: 'shipment-monitoring', action: 'read' },
  '/waiting-records': { module: 'waiting-records', action: 'read' },
  '/beklemeler': { module: 'waiting-records', action: 'read' },
  '/cost-analysis': { module: 'cost-analysis', action: 'read' },
  '/expense-submission': { module: 'expense-submission', action: 'read' },
  '/expense-approval': { module: 'expense-approval', action: 'read' },
  '/expense-categories': { module: 'expense-categories', action: 'read' },
  '/accounting-workflow': { module: 'accounting-workflow', action: 'read' },
  '/driver-mobile-earnings': { module: 'accounting-workflow', action: 'read' },
  '/driver-mobile-payrolls': { module: 'driver-tracking', action: 'read' },
  '/driver-mobile-home': { module: 'driver-tracking', action: 'read' },
  '/driver-mobile-assignments': { module: 'driver-tracking', action: 'read' },
  '/driver-mobile-documents': { module: 'documents', action: 'read' },
  '/driver-mobile-profile': { module: 'profile', action: 'read' },
  '/payment-orders': { module: 'payment-orders', action: 'read' },
  '/reports': { module: 'reports', action: 'read' },
  '/bekleme-bedeli-raporu': { module: 'reports', action: 'read' },
  '/return-load-potential-report': { module: 'return-load-potential-report', action: 'read' },
  '/flexible-reports': { module: 'flexible-reports', action: 'read' },
  '/warehouse-settings': { module: 'warehouse-settings', action: 'read' },
  '/ramp-management': { module: 'ramp-management', action: 'read' },
  '/base-prices': { module: 'base-prices', action: 'read' },
  '/support-center': { module: 'support-center', action: 'read' },
  '/help-desk-guide': { module: 'help-desk-guide', action: 'read' },
  '/bulk-sms': { module: 'whatsapp-send', action: 'read' },
  '/whatsapp-send': { module: 'whatsapp-send', action: 'read' },
  '/notifications': { module: 'push-notifications', action: 'read' },
  '/push-notifications': { module: 'push-notifications', action: 'read' },
  '/supply-metadata': { module: 'supply-metadata', action: 'read' },
  '/contract-management': { module: 'contract-management', action: 'read' },
  '/api-key-management': { module: 'api-key-management', action: 'read' },
  '/share-targets': { module: 'share-targets', action: 'read' },
  '/profile': { module: 'profile', action: 'read' },
  '/corporate-profile': { module: 'profile', action: 'read' },
  '/user-management': { module: 'user-management', action: 'read' },
  '/role-management': { module: 'role-management', action: 'read' },
  '/system-logs': { module: 'system-logs', action: 'read' },
  '/version-history': { module: 'version-history', action: 'read' },
  '/backup-management': { module: 'backup-management', action: 'read' },
  '/settings': { module: 'settings', action: 'read' },
  '/whatsapp-web': { module: 'whatsapp-web', action: 'read' },
  '/bid-marketplace': { module: 'bid-marketplace', action: 'read' },
  '/my-bids': { module: 'my-bids', action: 'read' },
  '/active-jobs': { module: 'active-jobs', action: 'read' },
  '/driver-tracking': { module: 'driver-tracking', action: 'read' },
  '/spot-driver-location-logs': { module: 'reports', action: 'read' },
  '/yatma-raporu': { module: 'reports', action: 'read' },
};

export const FEATURE_PERMISSION_RULES: Record<FeaturePermission, PermissionRule[]> = {
  canCreateSupply: [{ module: 'supply-management', action: 'create' }],
  canEditSupply: [{ module: 'supply-management', action: 'update' }],
  canDeleteSupply: [{ module: 'supply-management', action: 'delete' }],
  canCreateVehicle: [{ module: 'vehicle-management', action: 'create' }],
  canEditVehicle: [{ module: 'vehicle-management', action: 'update' }],
  canDeleteVehicle: [{ module: 'vehicle-management', action: 'delete' }],
  canCreatePersonnel: [{ module: 'drivers', action: 'create' }],
  canEditPersonnel: [{ module: 'drivers', action: 'update' }],
  canDeletePersonnel: [{ module: 'drivers', action: 'delete' }],
  canViewCostAnalysis: [{ module: 'cost-analysis', action: 'read' }],
  canExportReports: [{ module: 'reports', action: 'export' }],
  canManageUsers: [{ module: 'user-management', action: 'read' }],
  canViewSystemLogs: [{ module: 'system-logs', action: 'read' }],
  canConfigureIntegrations: [{ module: 'api-key-management', action: 'update' }, { module: 'settings', action: 'update' }],
  canViewOwnTasks: [{ module: 'driver-tracking', action: 'read' }],
  canUpdateTaskStatus: [{ module: 'driver-tracking', action: 'update' }],
  canUploadDocuments: [{ module: 'documents', action: 'create' }],
  canViewBidMarketplace: [{ module: 'bid-marketplace', action: 'read' }],
  canSubmitBid: [{ module: 'bid-marketplace', action: 'create' }],
  canViewOwnBids: [{ module: 'my-bids', action: 'read' }],
  canWithdrawBid: [{ module: 'my-bids', action: 'update' }],
  canManageWarehouse: [{ module: 'warehouse-settings', action: 'read' }],
  canScanDocuments: [{ module: 'documents', action: 'update' }],
};

export const MODULE_PERMISSION_ALIASES: Partial<Record<ModuleName, ModuleName[]>> = {
  'weighbridge-points': ['vehicle-management'],
  'vehicle-inspection-forms': ['vehicle-management'],
  'vehicle-assignment-forms': ['vehicle-assignments'],
  'driver-incident-reports': ['vehicle-management'],
  'traffic-fines': ['vehicle-management'],
  'seyir-driver-comparison': ['vehicle-management'],
  'personnel-files': ['drivers', 'personnel-management'],
  'driver-payrolls': ['drivers', 'personnel-management'],
  'return-load-potential-report': ['reports'],
  'flexible-reports': ['reports'],
  'help-desk-guide': ['support-center'],
};

export function normalizePermissionPath(path: string): string {
  const [withoutQuery] = String(path || '/').split('?');
  const [withoutHash] = withoutQuery.split('#');
  if (!withoutHash) return '/';
  if (withoutHash === '/') return '/';
  return withoutHash.endsWith('/') ? withoutHash.slice(0, -1) : withoutHash;
}

export function getRoutePermission(path: string): PermissionRule | null {
  const directPath = String(path || '/');
  if (ROUTE_PERMISSIONS[directPath]) return ROUTE_PERMISSIONS[directPath];
  return ROUTE_PERMISSIONS[normalizePermissionPath(path)] || null;
}

export const ROLE_INFO: Record<UserRole, { label: string; description: string; color: string }> = {
  admin: {
    label: 'Yönetici',
    description: 'Tam yetkili sistem yöneticisi',
    color: 'bg-red-100 text-red-800 border-red-200',
  },
  'super-admin': {
    label: 'Süper Admin',
    description: 'Silme ve süreçten geri alma dahil en yüksek operasyon yetkisi',
    color: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
  },
  'operations-manager': {
    label: 'Operasyon Sorumlusu',
    description: 'Teslim onay ve operasyonel muhasebe akislarini yonetir',
    color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  },
  dispatcher: {
    label: 'Dispozisyon Uzmanı',
    description: 'Planlama ve koordinasyon yetkisi',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  'internal-driver': {
    label: 'İç Sürücü',
    description: 'Filo şoförü - kendi görevleri',
    color: 'bg-green-100 text-green-800 border-green-200',
  },
  'external-driver': {
    label: 'Dış Sürücü',
    description: 'Spot şoför - kendi görevleri',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  'corporate-external-transport': {
    label: 'Kurumsal Dış Nakliye',
    description: 'Şirket bazlı dış nakliye operasyon hesabı',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  },
  'corporate-external-transport-user': {
    label: 'Kurumsal Dış Nakliye Alt Kullanıcı',
    description: 'Firma panel kullanıcısı',
    color: 'bg-sky-100 text-sky-800 border-sky-200',
  },
  'corporate-external-transport-driver': {
    label: 'Kurumsal Dış Nakliye Sürücü',
    description: 'Firma bağlı mobil sürücü',
    color: 'bg-violet-100 text-violet-800 border-violet-200',
  },
  warehouse: {
    label: 'Depo Görevlisi',
    description: 'Depo ve belge yönetimi',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
  },
};
